import {
  RenewalFilterByStatus,
  RenewalListItemsSortBy,
} from "@covertree/agent-portal-graphql";
import { Column } from "@covertree/ui/table/types";

export enum RemovableRenewalColumn {
  Type = "Type",
  Status = "Status",
  NonRenewalReason = "Non-Renewal Reason",
  NonRenewalDesc = "Non-Renewal Description",
  NonRenewalIssueDate = "Non-Renewal Issue Date",
  RenewalDiscardedDate = "Renewal Discarded Date",
  RenewalDiscardedBy = "Renewal Discarded By",
  PaymentMethod = "Payment Method",
  RenewalIssueDate = "Renewal Issue Date",
  Description = "Description",
}

export const allRenewalsColumns = [
  { headerName: "Policy ID" },
  { headerName: "Renewal ID" },
  { headerName: "Name" },
  { headerName: "Address" },
  { headerName: RemovableRenewalColumn.Type },
  { headerName: "Premium" },
  { headerName: RemovableRenewalColumn.Status },
  { headerName: RemovableRenewalColumn.NonRenewalReason },
  { headerName: RemovableRenewalColumn.NonRenewalIssueDate },
  {
    headerName: "Renewal Term",
    sortBy: RenewalListItemsSortBy.EffectiveDate,
  },
  { headerName: RemovableRenewalColumn.RenewalDiscardedDate },
  { headerName: "Assigned Agent" },
  { headerName: "Assigned Agency" },
  { headerName: RemovableRenewalColumn.RenewalIssueDate },
  {
    headerName: "Last Activity Date",
    sortBy: RenewalListItemsSortBy.UpdatedTimestamp,
  },
  { headerName: RemovableRenewalColumn.Description },
];

const nonRenewalColumns = [
  RemovableRenewalColumn.NonRenewalReason,
  RemovableRenewalColumn.NonRenewalDesc,
  RemovableRenewalColumn.NonRenewalIssueDate,
];

const discardedRenewalColumns = [
  RemovableRenewalColumn.RenewalDiscardedDate,
  RemovableRenewalColumn.RenewalDiscardedBy,
];

const removeColumnsFromAllFilter = [
  ...nonRenewalColumns,
  ...discardedRenewalColumns,
  RemovableRenewalColumn.Description,
];

const removeColumnsFromUpcomingRenewalFilter = [
  RemovableRenewalColumn.Status,
  ...nonRenewalColumns,
  ...discardedRenewalColumns,
  RemovableRenewalColumn.RenewalIssueDate,
  RemovableRenewalColumn.Description,
];

const removeColumnsFromUWReviewFilter = [
  RemovableRenewalColumn.Status,
  ...nonRenewalColumns,
  ...discardedRenewalColumns,
  RemovableRenewalColumn.RenewalIssueDate,
  RemovableRenewalColumn.Description,
];

const removeColumnsFromOfferSentFilter = [
  RemovableRenewalColumn.Status,
  ...nonRenewalColumns,
  ...discardedRenewalColumns,
  RemovableRenewalColumn.RenewalIssueDate,
  RemovableRenewalColumn.Description,
];

const removeColumnsFromRenewalIssuedFilter = [
  RemovableRenewalColumn.Type,
  RemovableRenewalColumn.Status,
  ...nonRenewalColumns,
  ...discardedRenewalColumns,
  RemovableRenewalColumn.Description,
];

const removeColumnsFromNonRenewalIssuedFilter = [
  RemovableRenewalColumn.Status,
  ...discardedRenewalColumns,
  RemovableRenewalColumn.RenewalIssueDate,
  RemovableRenewalColumn.Description,
];

const removeColumnsFromDiscardedRenewalFilter = [
  RemovableRenewalColumn.Status,
  ...nonRenewalColumns,
  RemovableRenewalColumn.RenewalIssueDate,
  RemovableRenewalColumn.Description,
];

const removeColumnsFromRenewalUWAcceptedFilter = [
  RemovableRenewalColumn.Status,
  ...nonRenewalColumns,
  ...discardedRenewalColumns,
  RemovableRenewalColumn.RenewalIssueDate,
  RemovableRenewalColumn.Description,
];

const removeColumnsFromRenewalFailedFilter = [
  ...nonRenewalColumns,
  ...discardedRenewalColumns,
  RemovableRenewalColumn.RenewalIssueDate,
  RemovableRenewalColumn.Description,
];

const filterToRemoveColumnsMap: Record<
  RenewalFilterByStatus,
  RemovableRenewalColumn[]
> = {
  [RenewalFilterByStatus.All]: removeColumnsFromAllFilter,
  [RenewalFilterByStatus.UpcomingRenewals]:
    removeColumnsFromUpcomingRenewalFilter,
  [RenewalFilterByStatus.UwReview]: removeColumnsFromUWReviewFilter,
  [RenewalFilterByStatus.OfferSent]: removeColumnsFromOfferSentFilter,
  [RenewalFilterByStatus.RenewalIssued]: removeColumnsFromRenewalIssuedFilter,
  [RenewalFilterByStatus.NonRenewalIssued]:
    removeColumnsFromNonRenewalIssuedFilter,
  [RenewalFilterByStatus.DiscardedRenewals]:
    removeColumnsFromDiscardedRenewalFilter,
  [RenewalFilterByStatus.RenewalUwAccepted]:
    removeColumnsFromRenewalUWAcceptedFilter,
  [RenewalFilterByStatus.Failed]: removeColumnsFromRenewalFailedFilter,
};

export const getColumnsToRemove = (filter: RenewalFilterByStatus) => {
  return filterToRemoveColumnsMap[filter];
};

export const createColumnValueChecker = (columns: Column[]) => {
  const availableColumns = columns.map(({ headerName }) => headerName);

  return (
    columnName: RemovableRenewalColumn,
    value: string | number | undefined | null
  ) => {
    return availableColumns.includes(columnName) ? [value ?? ""] : [];
  };
};
